import { createFileRoute } from '@tanstack/react-router';
import { Breadcrumbs } from '@components/navigation/Breadcrumb.tsx';
import SupplierLayout from '@components/layout/supplier/SupplierLayout.tsx';
import SupplierPriceList from '@features/supplier/prices/SupplierPriceList.tsx';
import { z } from 'zod';
import { useSupplierPriceStore } from '@features/supplier/prices/supplierPriceStore.ts';
import { PriceType } from '@api/generated/search-service';
import { useTranslation } from 'react-i18next';

const guaranteedFilterSchema = z.enum(['GUARANTEED', 'NOT_GUARANTEED', 'ALL']);

const supplierPricesSearchSchema = z.object({
  query: z.string().optional(),
  page: z.coerce.number().optional().default(1),
  organizationId: z.coerce.number().optional(),
  auctionId: z.coerce.number().optional(),
  guaranteedFilter: guaranteedFilterSchema.optional(),
  priceType: z.nativeEnum(PriceType).optional(),
});

export type SupplierPricesSearch = z.infer<typeof supplierPricesSearchSchema>;

export const Route = createFileRoute('/s/$orgId/$portalId/prices')({
  validateSearch: search => supplierPricesSearchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: ctx => {
    const { query, page, organizationId, auctionId, guaranteedFilter, priceType } = ctx.search;
    useSupplierPriceStore.getState().fetchPrices({
      query,
      page: page || 1,
      organizationId,
      auctionId,
      guaranteedFilter: guaranteedFilter || 'ALL',
      priceType,
    });
  },
});

function RouteComponent() {
  const { t } = useTranslation('client');
  return (
    <SupplierLayout>
      <div className="mb-8">
        <Breadcrumbs items={[{ label: t('supplierPrices.breadcrumb'), href: undefined }]} />
        <div className="flex items-center justify-between mb-2 mt-4">
          <h1 className="text-3xl font-bold text-main">{t('supplierPrices.pageTitle')}</h1>
        </div>
        <p className="text-light">{t('supplierPrices.pageDescription')}</p>
      </div>

      <SupplierPriceList />
    </SupplierLayout>
  );
}
